<template>
  <div class="col-sm-12">
    <div class="card custom-card">

      <!-- Staff Type Selection -->
      <div class="form-group">
        <label class="block mb-3 font-semibold">Staff Type :</label>

        <div v-if="isLoadingStaffTypes" class="loading-staff-types">
          <div class="spinner-blue"></div>
          <span>Loading staff types...</span>
        </div>

        <div v-else class="staff-type-container">
          <div
              v-for="staff in StaffTypeList"
              :key="staff.id"
              class="staff-type-box"
              :class="{ 'selected': selectedStaffType === staff.id }"
              @click="toggleStaffType(staff.id)"
          >
            <div class="staff-icon">
              <i :class="['fas', staff.icon || 'fa-user']"></i>
            </div>
            <div class="staff-label">{{ staff.staff_type || staff.staf_type }}</div>
            <div class="selected-indicator" v-if="selectedStaffType === staff.id">
              <i class="fas fa-check"></i>
            </div>
          </div>
        </div>
      </div>

      <!-- Dynamic Form Fields -->
      <div v-if="selectedStaffType" class="fields-container">
        
        <!-- Name (Driver/Helper only) -->
        <div class="field-item" v-if="isDriverOrHelper">
          <label for="name" class="block mb-2 font-semibold">Name :</label>
          <input type="text" id="name" v-model="name" placeholder="Enter name" class="name-input compact" :disabled="isSaving" />
        </div>

        <!-- First Name (Back Office, Sales Exec) -->
        <div class="field-item" v-if="isBackOffice || isSalesExecutive">
          <label for="first_name" class="block mb-2 font-semibold">First Name :</label>
          <input type="text" id="first_name" v-model="firstName" placeholder="Enter first name" class="name-input compact" :disabled="isSaving" />
        </div>

        <!-- Last Name (Back Office, Sales Exec) -->
        <div class="field-item" v-if="isBackOffice || isSalesExecutive">
          <label for="last_name" class="block mb-2 font-semibold">Last Name :</label>
          <input type="text" id="last_name" v-model="lastName" placeholder="Enter last name" class="name-input compact" :disabled="isSaving" />
        </div>

        <!-- Vehicle Number (Distributor Sales Rep) -->
        <div class="field-item" v-if="isDistributorSalesRep">
          <label for="vehicle_number" class="block mb-2 font-semibold">Vehicle Number :</label>
          <select id="vehicle_number" v-model="vehicleNumber" class="name-input compact" :disabled="isSaving">
            <option value="" disabled>Select Vehicle Number</option>
            <option v-for="vehicle in VehicleList" :key="vehicle.id" :value="vehicle.vehicle_no">
              {{ vehicle.vehicle_no }}
            </option>
          </select>
        </div>

        <!-- Mobile (Username) (Back Office, Sales Exec, Distributor Sales Rep) -->
        <div class="field-item" v-if="isBackOffice || isSalesExecutive || isDistributorSalesRep">
          <label for="mobile" class="block mb-2 font-semibold">Mobile (Username) :</label>
          <input type="text" inputmode="numeric" pattern="[0-9]*" maxlength="10" id="mobile" v-model="mobile" placeholder="Enter mobile number" class="name-input compact" :disabled="isSaving" @input="mobile = mobile.replace(/[^0-9]/g, '')" />
        </div>

        <!-- Role (Back Office) -->
        <div class="field-item" v-if="isBackOffice">
          <label for="role" class="block mb-2 font-semibold">Role :</label>
          <select id="role" v-model="selectedRole" class="name-input compact" :disabled="isSaving">
            <option :value="null" disabled>Select Role</option>
            <option v-for="r in UserRoleList" :key="r.id" :value="r.id">
              {{ r.name }}
            </option>
          </select>
        </div>


        <!-- Birthday (All Roles) -->
        <div class="field-item" v-if="isBackOffice || isSalesExecutive || isDistributorSalesRep || isDriverOrHelper">
          <label for="birthday" class="block mb-2 font-semibold">Birthday :</label>
          <input type="date" id="birthday" v-model="birthday" class="name-input compact" :disabled="isSaving" />
        </div>

      </div>

      <!-- Action Buttons -->
      <div class="d-flex justify-content-end action-buttons">
        <button
            type="button"
            class="btn btn-danger"
            @click="Cancel"
            :disabled="isSaving"
        >
          Cancel
        </button>
        <button
            type="button"
            class="btn btn-success save-button"
            @click="saveStaff"
            :disabled="isSaving || !isFormValid"
        >
          <span v-if="!isSaving">Save</span>
          <div v-else class="loading-indicator">
            <span class="spinner"></span>
            <span>Saving...</span>
          </div>
        </button>
      </div>

    </div>
  </div>
</template>

<script>
import { useRouter } from "vue-router";
import { fetchStaffType, saveStaffDetails, getDistributorVehicles, fetchDistributorUserRoleListForUserForm, saveUserDeatils } from "@/services/dataService";
import { useToast } from "vue-toastification";

export default {
  data() {
    return {
      token: localStorage.getItem('token_set') || '',
      profileId: localStorage.getItem('profile_id'),
      reportingProfileId: localStorage.getItem('reportingL1ProfileId'),
      userCategory: localStorage.getItem('user_label'),
      router: useRouter(),
      StaffTypeList: [],
      selectedStaffType: "",
      name: "",
      firstName: "",
      lastName: "",
      mobile: "",
      role: "",
      birthday: "",
      vehicleNumber: "",
      isSaving: false,
      isLoadingStaffTypes: false,
      selectedRole: null,
      UserRoleList: [],
      filteredRoles: [],
      VehicleList: [],
    }
  },

  computed: {
    selectedStaffTypeName() {
      const staff = this.StaffTypeList.find(s => s.id === this.selectedStaffType);
      return staff ? (staff.staff_type || staff.staf_type || '').toLowerCase() : '';
    },
    isBackOffice() {
      return this.selectedStaffTypeName.includes('back office');
    },
    isSalesExecutive() {
      return this.selectedStaffTypeName.includes('sales executive');
    },
    isDistributorSalesRep() {
      return this.selectedStaffTypeName.includes('sales rep');
    },
    isDriverOrHelper() {
      return this.selectedStaffTypeName.includes('driver') || this.selectedStaffTypeName.includes('helper');
    },
    isFormValid() {
      if (!this.selectedStaffType) return false;
      
      const fName = String(this.firstName || "").trim();
      const lName = String(this.lastName || "").trim();
      const mob = String(this.mobile || "").trim();
      const vNum = String(this.vehicleNumber || "").trim();
      const nName = String(this.name || "").trim();

      if (this.isBackOffice) {
        return fName && lName && mob && this.selectedRole && this.birthday;
      }
      if (this.isSalesExecutive) {
        return fName && lName && mob && this.birthday;
      }
      if (this.isDistributorSalesRep) {
        return vNum && mob && this.birthday;
      }
      if (this.isDriverOrHelper) {
        return nName && this.birthday;
      }
      
      return false;
    }
  },

  created() {
    this.loadStaffType();
    this.loadRoleList();

    let idToUse = this.profileId;
    if (this.userCategory === 'DISTRIBUTOR BACK OFFICE') {
      idToUse = this.reportingProfileId;
    }
    this.loadVehicleList(idToUse);
  },

  methods: {
    async loadStaffType() {
      this.isLoadingStaffTypes = true;
      try {
        const response = await fetchStaffType(this.token);
        this.StaffTypeList = response.data.data || [];
        if (this.StaffTypeList.length > 0 && !this.selectedStaffType) {
          this.selectedStaffType = this.StaffTypeList[0].id;
        }
        console.log('Fetched StaffList:', this.StaffTypeList);
      } catch (error) {
        console.error('Error fetching StaffList:', error);
      } finally {
        this.isLoadingStaffTypes = false;
      }
    },

    async loadVehicleList(profileId) {
      try {
        const response = await getDistributorVehicles(profileId, this.token);
        this.VehicleList = response.data.data || [];
      } catch (error) {
        console.error('Error fetching vehicle list:', error);
      }
    },

    toggleStaffType(staffId) {
      if (this.isSaving) return;

      // If clicking the already selected type, deselect it
      if (this.selectedStaffType === staffId) {
        this.selectedStaffType = "";
      } else {
        this.selectedStaffType = staffId;
      }
    },

    async loadRoleList() {
      try {
        const response = await fetchDistributorUserRoleListForUserForm(this.token);
        this.UserRoleList = response.data.data || [];
        this.filteredRoles = [...this.UserRoleList]; // Initialize filtered list
      } catch (error) {
        console.error('Error fetching user roles:', error);
      }
    },

    async saveStaff() {
      if (!this.isFormValid) {
        useToast().error("Please fill all required fields.");
        return;
      }

      // Set saving state
      this.isSaving = true;

      let idToUse = this.profileId;
      if (this.userCategory === 'DISTRIBUTOR BACK OFFICE') {
        idToUse = this.reportingProfileId;
      }

      if (this.isDriverOrHelper) {
        const payload = {
          staff_type: this.selectedStaffType,
          profile_id: idToUse,
          is_active: "1",
          name: this.name,
          birthday: this.birthday
        };

        try {
          await saveStaffDetails(this.token, payload);
          useToast().success("Staff added successfully!");
          this.$router.push('/distributor-staff');
        } catch (error) {
          console.error("Error response:", error.response);
          useToast().error("Failed to save staff. Please try again.");
        } finally {
          this.isSaving = false;
        }
      } else {
        let orgStructId = 0;
        let firstNameToUse = this.firstName;
        let lastNameToUse = this.lastName || '';
        let roleIdToUse = this.selectedRole;

        if (this.isBackOffice) {
          orgStructId = 16;
          roleIdToUse = this.selectedRole;
        } else if (this.isSalesExecutive) {
          orgStructId = 17;
          roleIdToUse = 13;
        } else if (this.isDistributorSalesRep) {
          orgStructId = 14;
          firstNameToUse = this.vehicleNumber;
          roleIdToUse = 2;
        }

        const payload = {
          dob: this.birthday,
          first_name: firstNameToUse,
          last_name: lastNameToUse,
          mobile: this.mobile,
          company_id: 1,
          org_struct_id: orgStructId,
          role_id: roleIdToUse,
          reporting_to: idToUse,
        };

        try {
          await saveUserDeatils(this.token, payload);
          useToast().success("User created successfully!");
          this.$router.push('/distributor-staff');
        } catch (error) {
          console.error(error);
          if (error.response && error.response.data && error.response.data.errors) {
            Object.values(error.response.data.errors).forEach((messages) => {
              messages.forEach((message) => useToast().error(message));
            });
          } else {
            useToast().error("Failed to save user data.");
          }
        } finally {
          this.isSaving = false;
        }
      }
    },

    Cancel() {
      if (!this.isSaving) {
        this.$router.push({ path: "/distributor-staff" });
      }
    }
  }
}
</script>

<style scoped lang="scss">
.custom-card {
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.form-group {
  margin-bottom: 24px;
  padding: 16px;
}

.staff-type-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 16px;
  margin-top: 12px;
}

.staff-type-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px 12px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  background-color: white;
  text-transform: uppercase;

  &:hover {
    border-color: #007bff;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  &.selected {
    border-color: #007bff;
    background-color: #e7f3ff;
  }
}

.staff-icon {
  font-size: 24px;
  margin-bottom: 8px;
  color: #6c757d;

  .selected & {
    color: #007bff;
  }
}

.staff-label {
  font-weight: 500;
  text-align: center;
  color: #495057;

  .selected & {
    color: #007bff;
    font-weight: 600;
  }
}

.selected-indicator {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 20px;
  height: 20px;
  background-color: #007bff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
}

.name-input {
  border: 1px solid #ced4da;
  border-radius: 4px;
  padding: 12px;
  width: 100%;
  margin-top: 8px;
  font-size: 16px;

  &:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }

  &:disabled {
    background-color: #e9ecef;
    opacity: 0.7;
    cursor: not-allowed;
  }
}

.name-input.compact {
  padding: 8px 12px;
  font-size: 14px;
  margin-top: 0;
}

.fields-container {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  padding: 16px;
  margin-bottom: 24px;
}

.field-item {
  display: flex;
  flex-direction: column;
}

.field-item.full-width {
  grid-column: 1 / -1;
}

.field-item label {
  font-size: 14px;
  margin-bottom: 4px;
}

.action-buttons {
  padding: 16px;
  gap: 12px;

  .btn {
    padding: 10px 20px;
    border-radius: 4px;
    font-weight: 500;
    min-width: 100px;

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
}

.save-button {
  position: relative;
  min-width: 120px;
}

.loading-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-staff-types {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 32px 0;
  color: #007bff;
  font-weight: 500;
}

.spinner-blue {
  width: 24px;
  height: 24px;
  border: 3px solid rgba(0, 123, 255, 0.2);
  border-top: 3px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .staff-type-container {
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  }

  .custom-card {
    padding: 16px;
  }

  .form-group, .fields-container {
    padding: 12px;
  }

  .fields-container {
    grid-template-columns: 1fr;
  }
}
</style>